'use client'

import type { ReactNode } from 'react'
import { EditorSurfaceProvider, type EditorSkin, type EditorSurfaceValue } from '@/deskUiSkeletons/editor/EditorSurfaceContext'
import { editorAttachmentsUiSkin, editorModalSkin, modernSettingsSkin } from '@/msgComponents/skins/editorModalSkin'
import { GALLERY_RICH_EDIT_VARIANT, RICH_EDIT_POLICY, galleryRichEditSkin } from './richEdit.config'

const galleryEditorSkin: EditorSkin = {
  headShort: '!hidden',
  head: {
    root: 'flex flex-col gap-2 border-b border-slate-200 bg-slate-50 px-4 pb-3 pt-4',
    nameKeyRow: 'flex min-w-0 items-center gap-2',
    fromLabel: '',
    recipientsRow: 'flex min-w-0 items-center gap-2',
    subjectRow: 'flex min-w-0 items-center gap-2',
    subjectLabel: 'flex-none text-xs font-medium text-slate-500',
    subjectInputBox: 'min-w-0 flex-1 rounded-lg border border-slate-300 bg-white',
    subjectInput: '!m-0 block !w-full box-border !rounded-lg !border-0 !bg-white !p-2 text-sm text-slate-900 shadow-none outline-none',
    compatControl: 'hidden pointer-events-none',
  },
  nameKey: {
    block: 'flex w-full flex-col gap-1',
    moreButton: '',
    advancedOpen: 'block',
    advancedClosed: 'hidden',
  },
  smartName: {
    root: 'flex w-full min-w-0 items-center gap-1.5 text-xs',
    sign: 'box-border h-7 flex-none rounded-l-lg border border-slate-300 bg-white px-2 pt-1.5 text-slate-500',
    signStatus: { busy: 'text-slate-700', ok: 'text-green-700', wrong: 'text-red-600' },
    inputBox: 'relative flex h-7 min-w-[235px] flex-1 items-center overflow-hidden rounded-lg border border-slate-300 bg-white',
    inputBoxWrong: 'border-red-600',
    input: '!m-0 h-6 !w-full !rounded-lg !border-0 !bg-white !p-1 text-sm text-slate-900 shadow-none outline-none placeholder:text-slate-400',
    actions: 'group relative box-border h-7 flex-none py-1 pl-0.5 pr-1',
    check: 'block cursor-pointer rounded px-1 py-0.5 text-violet-700 hover:underline',
    quick: 'absolute left-0 top-full z-[100005] hidden group-hover:block group-focus-within:block',
    quickItems: 'w-[150px] rounded-lg border border-slate-200 bg-white p-1 text-xs shadow-lg',
    quickItem: 'mb-0.5 inline-block cursor-pointer rounded bg-slate-50 p-1 text-slate-900',
  },
  extName: {
    root: 'flex flex-col gap-1 text-xs',
    quickRow: 'flex flex-wrap items-center gap-1',
    label: 'p-1 text-violet-700',
    quickBtn: 'cursor-pointer rounded-lg bg-slate-100 p-1 text-xs text-slate-900 hover:underline',
    subKeyBtn: 'cursor-pointer rounded-lg bg-violet-600 p-1 text-xs text-white hover:underline',
    box: 'relative max-w-[80%] overflow-hidden rounded-lg border border-slate-300 bg-white pr-8',
    textarea: '!m-0 block box-border min-h-[28px] !w-full resize-none !rounded-lg !border-0 !bg-white !p-2 text-sm text-slate-900 outline-none',
    mirror: 'hidden',
    checkRow: 'flex flex-wrap items-center gap-1.5',
    subKeyText: 'block rounded-lg bg-violet-600 p-1 text-xs text-white empty:hidden',
    check: 'cursor-pointer p-1 text-xs text-violet-700 hover:underline',
    emailInfo: 'p-1 text-[11px] text-slate-500',
    emailEdit: 'cursor-pointer p-1 text-[11px] text-violet-700 underline',
    code: 'hidden flex-wrap items-center gap-2 text-xs',
    codeInput: 'w-[250px] text-right text-xs',
    codeBtn: 'cursor-pointer p-1 text-violet-700',
    codeInfo: 'text-[11px] text-slate-500',
  },
  recipients: {
    root: 'flex w-full items-center gap-2 text-xs',
    label: 'flex-none text-xs font-medium text-slate-500',
    field: 'flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-hidden rounded-lg border border-slate-300 bg-white pr-8',
    inputWrap: 'min-w-[60px] flex-1 overflow-hidden',
    input: '!mt-0 block !w-full !rounded-lg !border-0 !bg-transparent !p-2 text-sm text-slate-900 shadow-none outline-none',
    inputErr: '!text-red-700 placeholder:text-red-700',
    inputErrHint: 'flex-none self-center whitespace-nowrap pr-2 text-[11px] text-red-700',
    list: 'my-1 ml-1 flex flex-wrap items-center gap-1',
    token: 'flex h-5 cursor-pointer items-center rounded-full px-2 leading-5 text-slate-700',
    tokenKind: { email: 'bg-blue-100', msg: 'bg-amber-100', self: 'bg-violet-600' },
    tokenText: 'max-h-5 text-xs font-bold text-violet-700',
    tokenTextSelf: 'text-white',
    tokenX: 'ml-1 cursor-pointer font-bold text-slate-500 hover:text-red-600',
    tokenLocked: '!cursor-default',
    tokenXSelf: 'text-violet-200',
    compat: 'hidden',
  },
  settings: modernSettingsSkin,
  body: {
    root: 'flex min-h-0 flex-1 flex-col bg-white',
    tools: 'flex min-h-10 flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 py-1.5',
    toolButton: 'cursor-pointer rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700',
    toolButtonActive: 'cursor-pointer rounded-lg border border-violet-600 bg-violet-600 px-2 py-1.5 text-xs text-white',
    legacyToolbarHidden: '',
    richFoldWrap: '',
    richFoldButton: '',
    textWrap: 'min-h-0 flex-1',
    textInner: 'h-full min-h-[420px]',
    textarea: 'box-border h-full min-h-[420px] w-full resize-none border-0 bg-white px-6 py-5 text-base leading-7 text-slate-900 outline-none',
    richSurface: 'box-border h-full min-h-[420px] w-full overflow-auto border-0 bg-white px-6 py-5 text-base leading-7 text-slate-900 outline-none',
    richToolbar: 'flex min-w-0 flex-wrap items-center gap-1',
    richButton: 'h-7 min-w-7 cursor-pointer rounded-lg border border-slate-300 bg-white px-1.5 text-xs leading-none text-slate-700 hover:bg-slate-100',
  },
  modal: editorModalSkin,
  attachmentsUi: editorAttachmentsUiSkin,
}

const galleryEditorSurface: EditorSurfaceValue = {
  variant: 'std',
  skin: galleryEditorSkin,
  richEditVariant: GALLERY_RICH_EDIT_VARIANT,
  richEditPolicy: RICH_EDIT_POLICY,
  richEditSkin: galleryRichEditSkin,
  richEditDefaultFormat: 'html',
}

export function Gallery2EditorSurfaceProvider({ children }: { children: ReactNode }) {
  return <EditorSurfaceProvider value={galleryEditorSurface}>{children}</EditorSurfaceProvider>
}
